// Component: Tooltip.jsx
// Purpose: DS tooltip — an inverted bubble shown on hover/focus. The bubble is
//   PORTALED to document.body and positioned with `position: fixed` off the
//   trigger's rect, so it escapes ANY clipping/blurred/scrolling ancestor
//   (e.g. the StrategyBuilder context header: overflow-hidden + backdrop-blur +
//   horizontal-scroll strip). Placement top/bottom/left/right. Styling (bubble
//   look) stays in ds-components.css (.ds-tooltip-bubble); positioning + reveal
//   are driven inline here. Wrap any focusable trigger.

import React, { useId, useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { cx } from "./cx";

//--> Centering transform per placement (constant) + a small closed-state nudge
//    that animates on reveal. top/left are set inline (no transition) so the
//    bubble tracks scroll without lag; only opacity + this transform animate.
const ANCHOR = {
  top:    "translate(-50%, -100%)",
  bottom: "translate(-50%, 0)",
  left:   "translate(-100%, -50%)",
  right:  "translate(0, -50%)",
};
const NUDGE = {
  top:    " translateY(4px)",
  bottom: " translateY(-4px)",
  left:   " translateX(4px)",
  right:  " translateX(-4px)",
};

//--> The bubble is portaled to <body>, escaping the trigger's themed subtree
//    (theme is [data-theme]-scoped). These theme-flipping tokens are copied from
//    the trigger's resolved style onto the bubble so the inverted bubble matches
//    the TRIGGER's theme, not document root's. (radius/font are global :root, so
//    they resolve fine at <body> and need no copy.)
const THEME_VARS = ["--fg-1", "--bg-1", "--shadow-md"];

export function Tooltip({ content, placement = "top", children, className, ...props }) {
  const id = useId();
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); //--> viewport coords for the fixed bubble
  const [themeVars, setThemeVars] = useState({}); //--> trigger's theme tokens, inlined on the bubble

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const gap = 6;
    const cx2 = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    if (placement === "bottom")     setPos({ top: r.bottom + gap, left: cx2 });
    else if (placement === "left")  setPos({ top: cy, left: r.left - gap });
    else if (placement === "right") setPos({ top: cy, left: r.right + gap });
    else                            setPos({ top: r.top - gap, left: cx2 }); // top (default)
  }, [placement]);

  //! On open: snapshot the trigger's theme tokens + measure; then keep the bubble
  //  pinned through scroll/resize (capture=true catches scrolling containers).
  useLayoutEffect(() => {
    if (!open) return;
    const el = triggerRef.current;
    if (el) {
      const cs = getComputedStyle(el);
      const vars = {};
      THEME_VARS.forEach((v) => {
        const val = cs.getPropertyValue(v);
        if (val) vars[v] = val;
      });
      setThemeVars(vars);
    }
    measure();
    const onMove = () => measure();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, measure]);

  //--> No content → render the trigger untouched.
  if (content == null || content === "") return children;

  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const transform = (ANCHOR[placement] || ANCHOR.top) + (open ? "" : (NUDGE[placement] || NUDGE.top));

  return (
    <span
      ref={triggerRef}
      className={cx("ds-tooltip", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      {...props}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children, { "aria-describedby": open ? id : undefined })
        : children}
      {pos &&
        createPortal(
          <span
            role="tooltip"
            id={id}
            className="ds-tooltip-bubble"
            style={{ position: "fixed", top: pos.top, left: pos.left, transform, opacity: open ? 1 : 0, ...themeVars }}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
}

export default Tooltip;
